export const COMPLIANCE_TRIAL_ACCESS_SCOPE = 'COMPLIANCE_TRIAL' as const;
export const COMPLIANCE_TRIAL_SCOPE_MARKER = '@scope/compliance_trial' as const;
export const COMPLIANCE_TRIAL_SCOPE_COOKIE = 'statura_compliance_trial_scope' as const;
export const COMPLIANCE_TRIAL_TRUSTED_REQUEST_HEADER = 'x-statura-compliance-trial-scope' as const;
export const NON_COMPLIANCE_TRIAL_ACCESS_SCOPE = 'NON_COMPLIANCE_TRIAL' as const;

const COMPLIANCE_TRIAL_SETTINGS_PREFIXES = [
  '/settings/document-templates',
  '/settings/facilities',
  '/settings/notification-audit',
  '/settings/notification-preferences',
  '/settings/notifications',
  '/settings/organisation',
  '/settings/registration-providers',
  '/settings/retention',
  '/settings/security',
  '/settings/users',
  // Wave 5 slice 2 (2026-09-21, deliberate, narrow addition — see
  // docs/source-of-truth/access-control-and-module-gating.md "Compliance-only
  // trial boundary"): a trial user, expired or active, must be able to reach
  // Settings > Billing to convert to a paid Essentials plan.
  // ModuleGate's "Go to Billing" CTA already pushes '/settings/billing' for
  // an inactive subscriber; without this entry the compliance trial's own
  // middleware boundary (complianceTrialBoundaryResponse, which is driven
  // entirely by isComplianceTrialRouteAllowed) bounced that navigation back
  // to '/overview?scope=compliance_trial', producing a redirect loop. This is
  // the ONLY new entry this slice adds to the trial allowlist — checkout,
  // Stripe webhook handling, and every other trial route stay unchanged.
  '/settings/billing',
  '/settings/compliance',
] as const;

const COMPLIANCE_TRIAL_DASHBOARD_PREFIXES = [
  '/dashboards/compliance',
  '/compliance/calendar',
] as const;

// Compliance itself contains several separately licensed clinical, workforce,
// residential, prudential, IPC, WHS and NDIS products. Trial routing therefore
// uses a positive list instead of inheriting every child below /compliance.
// This list mirrors the deliberately curated trial module set.
const COMPLIANCE_TRIAL_COMPLIANCE_PREFIXES = [
  '/compliance/work',
  '/compliance/incidents',
  '/compliance/open-disclosure',
  '/compliance/quality-indicators',
  '/compliance/audit',
  '/compliance/audit-program',
  '/compliance/audit-trail',
  '/compliance/governance',
  '/compliance/policies',
  '/compliance/environmental',
  '/compliance/complaints',
  '/compliance/whistleblower',
  '/compliance/data-breach',
  '/compliance/responsible-persons',
  '/compliance/continuous-improvement',
  '/compliance/documents',
  '/compliance/registration',
  '/compliance/provider-obligations',
  '/compliance/restrictive-practices',
] as const;

const COMPLIANCE_TRIAL_API_PREFIXES = [
  '/api/compliance/incidents',
  '/api/compliance/whistleblower',
] as const;

const COMPLIANCE_TRIAL_EXACT_API_PATHS = new Set([
  '/api/customer-legal',
  '/api/health',
  '/api/help/chat',
  '/api/notifications/acknowledge',
]);

// These children sit beneath otherwise legitimate compliance parents but
// execute workforce/payroll or Support at Home data flows. They are separate
// paid-package capabilities and must not be inherited by prefix during the
// compliance-only trial.
const COMPLIANCE_TRIAL_DENIED_PREFIXES = [
  '/compliance/audit-pack',
  // The conduct family is a workforce workflow: acknowledgments and breach
  // investigations are bound to staff records. A compliance-only tenant has
  // no workforce entitlement, so exposing this parent would create a broken
  // UI and invite staff-table expansion through PostgREST embeds.
  '/compliance/code-of-conduct',
  '/compliance/standards?tab=tagged-evidence',
  '/compliance/reporting',
  '/compliance/star-ratings',
  '/dashboards/quality',
  '/api/compliance/audit-pack',
  '/api/compliance/reporting',
  '/api/compliance/star-ratings',
  '/settings/approvals',
  '/settings/escalations',
  '/settings/facility-config',
  '/settings/notification-routing',
  '/settings/signature-templates',
  '/tasks/new',
  '/tasks/signatures',
  '/workflows/instances',
] as const;

function matchesPathPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

function isComplianceClientRoute(pathname: string): boolean {
  if (pathname === '/clients' || pathname === '/clients/new') return true;

  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'clients' || !segments[1]) return false;

  // Permit the base client record and its incident ledger only. Clinical,
  // medication, care-plan, SAH, schedule, and assessment children stay out of
  // the compliance trial even though the client registry itself is required
  // to give incidents and complaints real context.
  return segments.length === 2 || (segments.length === 3 && segments[2] === 'incidents');
}

/**
 * Strict route boundary for users belonging to a compliance-only trial.
 * Normal paid-tier users never enter this branch.
 */
export function isComplianceTrialRouteAllowed(pathname: string): boolean {
  // Callers normally pass a pathname; entitlement manifests may include a view query.
  if (pathname.includes('?'))
    return isComplianceTrialRouteAllowed(pathname.split('?')[0] ?? pathname);
  if (COMPLIANCE_TRIAL_DENIED_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix))) {
    return false;
  }
  if (
    pathname === '/' ||
    pathname === '/overview' ||
    pathname === '/settings' ||
    pathname === '/compliance'
  ) {
    return true;
  }
  if (COMPLIANCE_TRIAL_COMPLIANCE_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix))) {
    return true;
  }
  if (isComplianceClientRoute(pathname)) return true;
  if (COMPLIANCE_TRIAL_SETTINGS_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix))) {
    return true;
  }
  if (COMPLIANCE_TRIAL_DASHBOARD_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix))) {
    return true;
  }

  if (
    matchesPathPrefix(pathname, '/help') ||
    matchesPathPrefix(pathname, '/tasks') ||
    pathname === '/me/profile' ||
    matchesPathPrefix(pathname, '/access')
  ) {
    return true;
  }

  // Compliance APIs retain their own authentication, role, tenant, and rate
  // checks. Every unrelated API is denied for this scoped user population.
  return (
    COMPLIANCE_TRIAL_EXACT_API_PATHS.has(pathname) ||
    COMPLIANCE_TRIAL_API_PREFIXES.some((prefix) => matchesPathPrefix(pathname, prefix))
  );
}

export function isComplianceTrialAccessScopeTenant(input: {
  billingPlan: string | null | undefined;
  subscriptionStatus: string | null | undefined;
  isDemo: boolean | null | undefined;
  selectedPackages: unknown;
  brandConfig: unknown;
}): boolean {
  const brandConfig =
    input.brandConfig && typeof input.brandConfig === 'object' && !Array.isArray(input.brandConfig)
      ? (input.brandConfig as Record<string, unknown>)
      : {};
  const packages = Array.isArray(input.selectedPackages) ? input.selectedPackages : [];

  return (
    input.billingPlan == null &&
    (input.subscriptionStatus === 'trialing' || input.subscriptionStatus === 'trial_expired') &&
    input.isDemo !== true &&
    packages.length === 1 &&
    packages[0] === 'compliance_trial' &&
    brandConfig.trialAccessScope === 'compliance_only'
  );
}

export function isComplianceTrialModuleSet(
  enabledModules: readonly string[] | null | undefined,
): boolean {
  return enabledModules?.includes(COMPLIANCE_TRIAL_SCOPE_MARKER) ?? false;
}
